import * as React from "react";
import { QESCheckbox, QESSelect } from "@app/shared/qes-standards";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { cloneDeep, uniq } from "lodash";
import { ColumnConfig, TextAlignment, Waffles, wafflesKeyColumnName } from "../../../shared/waffles";
import { ClientSoftwareReleaseModel } from "../../../client/client";
import { JSX } from "react";

interface ClientSoftwareReleasesProps {
  disabled?: boolean;
}

interface ClientSoftwareReleasesTableData {
  releaseQuarter: number;
  dateReleased: string;
  releasedToClient: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  dateReleased: {
    alignment: TextAlignment.Center,
    displayText: "Release Date",
    noResize: true,
    noSort: true,
    testId: "release-date",
  },
  releaseQuarter: {
    alignment: TextAlignment.Center,
    displayText: "Release Quarter",
    noResize: true,
    testId: "release-quarter",
  },
  releasedToClient: {
    alignment: TextAlignment.Center,
    displayText: "Released to Client?",
    noResize: true,
    noSort: true,
    testId: "released-to-client",
  },
};


export const ClientSoftwareReleases: React.FC<React.PropsWithChildren<ClientSoftwareReleasesProps>> = (props: ClientSoftwareReleasesProps) => {

  const clientContext = React.useContext(ClientContext);

  const releaseYears = React.useMemo(() => {
    const releaseYears: number[] = [];
    if (clientContext.softwareReleases) {
      return uniq(clientContext.softwareReleases.map((sr) => sr.releaseYear));
    }

    return releaseYears;
  }, [clientContext.softwareReleases])

  const [releasesTableData, setReleasesTableData] = React.useState<ClientSoftwareReleasesTableData[]>([]);

  const toggleSoftwareRelease = (softwareReleaseId: number) => {
    const updatedSoftwareReleases = cloneDeep(clientContext.softwareReleases);
    const softwareRelease = updatedSoftwareReleases.find((sr) => sr.softwareReleaseId == softwareReleaseId);
    softwareRelease.releasedToClient = !softwareRelease.releasedToClient;
    clientContext.setSoftwareReleases(updatedSoftwareReleases);
  }

  //const releaseSoftware = (softwareReleaseId: number) => {
  //  const updatedSoftwareReleases = cloneDeep(clientContext.softwareReleases);
  //  updatedSoftwareReleases.find((sr) => sr.softwareReleaseId == softwareReleaseId).releasedToClient = true;
  //  clientContext.setSoftwareReleases(updatedSoftwareReleases);
  //}

  const buildTableData = (data: ClientSoftwareReleaseModel[]): ClientSoftwareReleasesTableData[] => {
    return data.map<ClientSoftwareReleasesTableData>((sr, index) => {
      /* eslint-disable sort-keys */
      return {
        releaseQuarter: sr.releaseQuarter,
        dateReleased: new Date(sr.dateReleased).toLocaleDateString(),
        releasedToClient:
          (<>
            <QESCheckbox
              label=""
              checked={!!sr.releasedToClient}
              onChange={() => { toggleSoftwareRelease(sr.softwareReleaseId); }}
              testId={`software-released-to-client--${index}`}
            />
          </>),
        [wafflesKeyColumnName]: sr.softwareReleaseId.toString(),
      };
      /* eslint-enable sort-keys */
    });
  }

  const onReleaseYearChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const releaseYear = parseInt(e.target.value, 10);
    clientContext.setSelectedReleaseYear(releaseYear);
  }

  const onHoldSoftwareReleaseChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    clientContext.setHoldSoftwareRelease(e.target.checked);
  }

  React.useEffect(() => {
    const releases = clientContext.softwareReleases.filter((sr) => sr.releaseYear === clientContext.selectedReleaseYear);
    setReleasesTableData(buildTableData(releases))
  }, [clientContext.softwareReleases, clientContext.selectedReleaseYear]);

  return (
    <>
      <div>
        <QESCheckbox
          containerClassName="ml-2 mr-2"
          testId="client-software-release__hold-software-release"
          label="Hold Software Release"
          onChange={onHoldSoftwareReleaseChanged}
          checked={clientContext.holdSoftwareRelease}

        />
      </div>
      <div className="row">
        <div className="col-2">
          <QESSelect
            containerClassName="col-12"
            label="Release Year"
            onChange={onReleaseYearChanged}
            value={clientContext.selectedReleaseYear}
            testId="new-client__release-year"
            disabled={props.disabled}
          >
            [<option value="-1" key="-1" />, ...{releaseYears.map((y) => <option value={y} key={y}>{y}</option>)}]
          </QESSelect>
        </div>
      </div>
      {clientContext.selectedReleaseYear > 0 && (
        <div className="row mt-3">
          <div className="col-12">
            <Waffles<ClientSoftwareReleasesTableData>
              columns={columnConfig}
              //customSort={customSort}
              data={releasesTableData}
              emptyTableMessage="There are no Software Releases for the selected Year."
              fullWidth={true}
              testId="new-client__releases-table"
            />
          </div>
        </div>
      )}
    </>
  );

}
